import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { useMemo, useState } from "react";
import type { Category, Subcategory, SubSubcategory } from "../lib/receipts";

/**
 * Volltext-suchbarer Kategorie-Pfad-Selektor.
 *
 * Ersetzt die 2 nativen `<select>` (Category + Subcategory) durch EINE Combobox,
 * in der jeder wählbare Endpunkt (Cat / Cat+Subcat / Cat+Subcat+SubSub) mit
 * vollem Pfad sichtbar ist und nach beliebigem Segment durchsucht werden kann.
 *
 * "Geschenke" tippen → findet sowohl die Top-Cat "Geschenke" (Einnahmen-Seite)
 * als auch die Subcat "Lebensführung › Geschenke" — disambiguiert durch den Pfad.
 *
 * Beim Auswählen wird `onChange` mit ALLEN 3 IDs aufgerufen (categoryId,
 * subcategoryId, subSubcategoryId — jeweils mit oder ohne Wert je nach Tiefe).
 * Der konsumierende Code (ReceiptDetailPage) macht die drei PATCH-Aufrufe.
 */

export type CategoryPathSelection = {
  categoryId: string | null;
  subcategoryId: string | null;
  subSubcategoryId: string | null;
};

type PathEntry = {
  key: string;
  categoryId: string;
  subcategoryId: string | null;
  subSubcategoryId: string | null;
  pathSegments: string[]; // ["Lebensführung", "Nahrung"] etc.
  haystack: string; // alle Segmente klein + zusammen, für Suche
};

function buildPaths(
  categories: Category[],
  subcategories: Subcategory[],
  subSubcategories: SubSubcategory[],
): PathEntry[] {
  const subsByParent = new Map<string, Subcategory[]>();
  for (const s of subcategories) {
    if (s.archived) continue;
    const arr = subsByParent.get(s.parentId) ?? [];
    arr.push(s);
    subsByParent.set(s.parentId, arr);
  }
  const subSubsByParent = new Map<string, SubSubcategory[]>();
  for (const ss of subSubcategories) {
    if (ss.archived) continue;
    const arr = subSubsByParent.get(ss.parentId) ?? [];
    arr.push(ss);
    subSubsByParent.set(ss.parentId, arr);
  }

  // Migration 0012 / Leaf-Konstraint (2026-06-05): Nur Pfad-Enden werden auswählbar gemacht.
  // Begründung: Top-Cats wie „Versicherungen u. Finanzen" oder „Schulden" sind Sammler-
  // Bezeichner, kein echtes Klassifikations-Ende. User-Wunsch: im Beleg-Dropdown nur
  // ADAC, Dispozinsen, „Rate Überbrückungsdarlehen" anzubieten — nicht ihre Wurzeln.
  // Auf Settings-Ebene wandert auch stability/isNeutral auf das Leaf.
  const out: PathEntry[] = [];
  for (const c of categories) {
    if (c.archived) continue;
    const subs = subsByParent.get(c.id) ?? [];
    if (subs.length === 0) {
      // Cat ist Leaf (keine aktiven Subs) → wählbar
      out.push({
        key: `c:${c.id}`,
        categoryId: c.id,
        subcategoryId: null,
        subSubcategoryId: null,
        pathSegments: [c.name],
        haystack: c.name.toLowerCase(),
      });
      continue;
    }
    // Cat ist Non-Leaf → selbst NICHT wählbar, weiter zu Subs
    for (const s of subs) {
      const subSubs = subSubsByParent.get(s.id) ?? [];
      if (subSubs.length === 0) {
        // Sub ist Leaf (keine aktiven SubSubs) → wählbar
        out.push({
          key: `c:${c.id}|s:${s.id}`,
          categoryId: c.id,
          subcategoryId: s.id,
          subSubcategoryId: null,
          pathSegments: [c.name, s.name],
          haystack: `${c.name} ${s.name}`.toLowerCase(),
        });
        continue;
      }
      // Sub ist Non-Leaf → selbst NICHT wählbar, weiter zu SubSubs (immer Leaf)
      for (const ss of subSubs) {
        out.push({
          key: `c:${c.id}|s:${s.id}|ss:${ss.id}`,
          categoryId: c.id,
          subcategoryId: s.id,
          subSubcategoryId: ss.id,
          pathSegments: [c.name, s.name, ss.name],
          haystack: `${c.name} ${s.name} ${ss.name}`.toLowerCase(),
        });
      }
    }
  }
  // Sortierung: alphabetisch nach dem LETZTEN Pfad-Segment, damit „Strom" am gleichen
  // Ort steht egal ob `Wohnen › Energie › Strom` oder `Alte Wohnung › Strom`.
  out.sort((a, b) => {
    const la = a.pathSegments[a.pathSegments.length - 1]!.toLowerCase();
    const lb = b.pathSegments[b.pathSegments.length - 1]!.toLowerCase();
    if (la !== lb) return la.localeCompare(lb);
    return a.pathSegments.join(" / ").localeCompare(b.pathSegments.join(" / "));
  });
  return out;
}

export function CategoryPathCombobox({
  selection,
  categories,
  subcategories,
  subSubcategories,
  onChange,
  className,
  disabled,
  placeholder = 'Kategorie suchen — z.B. „Strom", „Geschenke", „Müll"',
}: {
  selection: CategoryPathSelection;
  categories: Category[];
  subcategories: Subcategory[];
  subSubcategories: SubSubcategory[];
  onChange: (next: CategoryPathSelection) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}) {
  const paths = useMemo(
    () => buildPaths(categories, subcategories, subSubcategories),
    [categories, subcategories, subSubcategories],
  );

  // Aktuell gewählter PathEntry (anhand der IDs in selection finden)
  const current = useMemo(() => {
    if (!selection.categoryId) return null;
    const key = selection.subSubcategoryId
      ? `c:${selection.categoryId}|s:${selection.subcategoryId}|ss:${selection.subSubcategoryId}`
      : selection.subcategoryId
      ? `c:${selection.categoryId}|s:${selection.subcategoryId}`
      : `c:${selection.categoryId}`;
    return paths.find((p) => p.key === key) ?? null;
  }, [paths, selection]);

  const [query, setQuery] = useState("");

  // Such-Filter: tokenize Query, jedes Token muss im Haystack vorkommen.
  // Damit findet „strom wohnen" auch `Wohnen › Energie › Strom` (Reihenfolge egal).
  const filtered = useMemo(() => {
    if (!query.trim()) return paths.slice(0, 200); // Performance-Cap fürs UI
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    return paths.filter((p) => tokens.every((t) => p.haystack.includes(t))).slice(0, 200);
  }, [paths, query]);

  return (
    <Combobox
      value={current}
      onChange={(p: PathEntry | null) => {
        if (!p) {
          onChange({ categoryId: null, subcategoryId: null, subSubcategoryId: null });
          return;
        }
        onChange({
          categoryId: p.categoryId,
          subcategoryId: p.subcategoryId,
          subSubcategoryId: p.subSubcategoryId,
        });
      }}
      disabled={disabled}
    >
      <div className={`relative ${className ?? ""}`}>
        <ComboboxInput
          // iOS-Auto-Zoom-Schutz: ≥16px auf Mobile zwingt Safari nicht zum Reinzoomen
          // (Konvention der App, dokumentiert in [[feedback_responsive_grid_breakpoint_height_trap]]).
          className="w-full bg-ios-bg/60 text-[16px] lg:text-[14px] px-2 py-1 rounded-ios focus:ring-1 focus:ring-ios-blue outline-none disabled:opacity-40 truncate"
          displayValue={(p: PathEntry | null) => (p ? p.pathSegments.join(" › ") : "")}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          title={current ? current.pathSegments.join(" › ") : undefined}
        />
        <ComboboxOptions
          anchor="bottom start"
          // Mobile: Dropdown füllt den Viewport (iPhone-Portrait = 375px, 28rem=448px sprengt sonst).
          // Desktop ab md: feste Min-Width 28rem für komfortable Pfad-Anzeige.
          className="z-50 mt-1 min-w-[calc(100vw-2rem)] md:min-w-[28rem] md:w-[var(--input-width)] max-h-80 overflow-auto rounded-ios bg-white shadow-lg ring-1 ring-ios-line/40 py-1 empty:hidden"
        >
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-[14px] text-ios-muted">Keine Treffer</div>
          )}
          {filtered.map((p) => (
            <ComboboxOption
              key={p.key}
              value={p}
              className="cursor-pointer px-3 py-1.5 text-[14px] data-[focus]:bg-ios-blue data-[focus]:text-white data-[selected]:font-semibold"
            >
              <div className="flex items-baseline gap-1.5">
                <span className="font-medium">{p.pathSegments[p.pathSegments.length - 1]}</span>
                {p.pathSegments.length > 1 && (
                  <span className="text-[12px] opacity-70 truncate">
                    in {p.pathSegments.slice(0, -1).join(" › ")}
                  </span>
                )}
              </div>
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}
